/**
 * ProgressPushRunDetailPage — /progress-push/runs/:id
 *
 * Detail view of one ProgressPushRun. Per-record Approve/Reject buttons and
 * a run-level "Execute Approved (DRY RUN)" button with a confirmation modal
 * that makes dry-run semantics unambiguous.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Divider,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  PlayArrow as ExecuteIcon,
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { DryRunBanner } from './ProgressPushRunsPage';

const APPROVAL_COLOR = {
  pending: 'default',
  approved: 'success',
  rejected: 'error',
  written_mock: 'warning',
  written_live: 'error',
};

const DIFF_COLOR = {
  new_progress: 'info',
  progress_update: 'primary',
  completion_event: 'success',
  noop: 'default',
  conflict: 'error',
};

function JsonBlock({ value, maxHeight = 200 }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1,
        bgcolor: '#f5f5f5',
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        fontSize: 11,
        fontFamily: 'monospace',
        whiteSpace: 'pre-wrap',
        maxHeight,
        overflow: 'auto',
      }}
    >
      {value == null ? '(none)' : JSON.stringify(value, null, 2)}
    </Box>
  );
}

export default function ProgressPushRunDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [run, setRun] = useState(null);
  const [records, setRecords] = useState([]);
  const [liveMode, setLiveMode] = useState(false);
  const [warning, setWarning] = useState('DRY RUN — no writes to production DBs');

  const [rejectDialog, setRejectDialog] = useState({ open: false, recordId: null, reason: '' });
  const [executeDialog, setExecuteDialog] = useState(false);
  const [busy, setBusy] = useState(false);
  const [snack, setSnack] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`/api/progress-push/runs/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setRun(data.run);
      setRecords(data.records || []);
      setLiveMode(!!data.liveMode);
      setWarning(data.warning || '');
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (recordId) => {
    const token = localStorage.getItem('token');
    setBusy(true);
    try {
      const resp = await fetch(`/api/progress-push/records/${recordId}/approve`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setSnack('Record approved (DRY RUN — no prod write)');
      await load();
    } catch (e) {
      setSnack(`Approve failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const doReject = async () => {
    const token = localStorage.getItem('token');
    setBusy(true);
    try {
      const resp = await fetch(
        `/api/progress-push/records/${rejectDialog.recordId}/reject`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ reason: rejectDialog.reason }),
        },
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setSnack('Record rejected');
      setRejectDialog({ open: false, recordId: null, reason: '' });
      await load();
    } catch (e) {
      setSnack(`Reject failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const doExecuteApproved = async () => {
    const token = localStorage.getItem('token');
    setBusy(true);
    try {
      const resp = await fetch(
        `/api/progress-push/runs/${id}/execute-approved`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setSnack(
        `Executed (DRY RUN): processed=${data.processed}, writtenMock=${data.writtenMock}, writtenLive=${data.writtenLive}`,
      );
      setExecuteDialog(false);
      await load();
    } catch (e) {
      setSnack(`Execute failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!run) return <Alert severity="warning">Run not found</Alert>;

  const approvedCount = records.filter((r) => r.approvalState === 'approved').length;

  return (
    <Box sx={{ p: 2 }}>
      <DryRunBanner liveMode={liveMode} warning={warning} />

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate('/progress-push')} sx={{ mr: 1 }}>
          <BackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          Run {run.id.slice(0, 8)} — {run.appConfigKey} — client {run.clientId}
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={load} disabled={busy}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Button
          variant="contained"
          color="warning"
          startIcon={<ExecuteIcon />}
          onClick={() => setExecuteDialog(true)}
          disabled={busy || approvedCount === 0}
          sx={{ ml: 2 }}
        >
          Execute Approved (DRY RUN) — {approvedCount}
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Summary
        </Typography>
        <JsonBlock value={run.summaryStats} maxHeight={160} />
      </Paper>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Records ({records.length})
      </Typography>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Diff</TableCell>
              <TableCell>Approval</TableCell>
              <TableCell>User Hints</TableCell>
              <TableCell>Current vs Proposed</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((r) => (
              <TableRow key={r.id}>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                  {r.id.slice(0, 8)}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={r.diffType} color={DIFF_COLOR[r.diffType] || 'default'} />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={r.approvalState}
                    color={APPROVAL_COLOR[r.approvalState] || 'default'}
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 180 }}>
                  <JsonBlock value={r.identifierHints} maxHeight={100} />
                </TableCell>
                <TableCell sx={{ minWidth: 400 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Current (from Nucleos)
                      </Typography>
                      <JsonBlock value={r.currentState} maxHeight={160} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Proposed (dry-run)
                      </Typography>
                      <JsonBlock value={r.proposedPayload} maxHeight={160} />
                    </Box>
                  </Box>
                  {r.mockWriteResult && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Mock Write Result
                      </Typography>
                      <JsonBlock value={r.mockWriteResult} maxHeight={100} />
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title="Approve (dry run — no prod write)">
                    <span>
                      <IconButton
                        size="small"
                        color="success"
                        disabled={busy || r.approvalState === 'approved' || r.approvalState === 'written_mock' || r.approvalState === 'written_live'}
                        onClick={() => approve(r.id)}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Reject">
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={busy || r.approvalState === 'rejected'}
                        onClick={() =>
                          setRejectDialog({ open: true, recordId: r.id, reason: '' })
                        }
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Reject dialog */}
      <Dialog
        open={rejectDialog.open}
        onClose={() => setRejectDialog({ open: false, recordId: null, reason: '' })}
      >
        <DialogTitle>Reject Record</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Rejecting only marks this staging record rejected — it does not
            touch any Nucleos production database.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Reason"
            value={rejectDialog.reason}
            onChange={(e) =>
              setRejectDialog((d) => ({ ...d, reason: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRejectDialog({ open: false, recordId: null, reason: '' })}
          >
            Cancel
          </Button>
          <Button onClick={doReject} color="error" disabled={busy}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Execute Approved dialog */}
      <Dialog open={executeDialog} onClose={() => setExecuteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <WarningIcon sx={{ verticalAlign: 'middle', mr: 1, color: '#ed6c02' }} />
          Execute {approvedCount} Approved Records — DRY RUN
        </DialogTitle>
        <DialogContent>
          <Alert severity={liveMode ? 'error' : 'warning'} sx={{ mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 'bold' }}>
              {liveMode
                ? 'LIVE MODE IS ENABLED — writes will hit production'
                : 'DRY RUN MODE — No data will be written to production databases'}
            </AlertTitle>
            {liveMode
              ? 'The PROGRESS_PUSH_LIVE env flag is "true". Proceeding will attempt real database writes.'
              : 'Approved records will be recorded as written_mock. Their proposed payloads are captured in mockWriteResult. No Nucleos production DB will be touched.'}
          </Alert>
          <DialogContentText>
            Execute {approvedCount} approved records for this run?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExecuteDialog(false)}>Cancel</Button>
          <Button
            onClick={doExecuteApproved}
            variant="contained"
            color={liveMode ? 'error' : 'warning'}
            disabled={busy}
          >
            {liveMode ? 'Execute LIVE' : 'Execute (DRY RUN)'}
          </Button>
        </DialogActions>
      </Dialog>

      {snack && (
        <Alert
          severity="info"
          sx={{ position: 'fixed', bottom: 16, right: 16, boxShadow: 3 }}
          onClose={() => setSnack(null)}
        >
          {snack}
        </Alert>
      )}
    </Box>
  );
}
